// src/pages/pentest/dashboard/Assets.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha, lighten } from '@mui/material/styles';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import Nav from './nav';
import { Bar, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

// ----- Helpers -----
const cellLabelSX = { fontSize: 12, color: 'text.secondary' };
const surfaceCardSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(PRIMARYCOLOR, 0.08),
  boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
  bgcolor: '#fff',
};

const safeParseList = (value) => {
  if (!value) return [];

  // Already an array ✅
  if (Array.isArray(value)) {
    return value
      .map((v) => String(v).trim())
      .filter(Boolean)
      .map((v) => v.replace(/:+$/, '')); // remove trailing "::::"
  }

  // Sometimes backend returns stringified JSON
  if (typeof value === 'string') {
    const trimmed = value.trim();

    // JSON array string -> parse
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v) => String(v).trim())
            .filter(Boolean)
            .map((v) => v.replace(/:+$/, ''));
        }
      } catch {
        // fall through
      }
    }

    // Comma separated fallback
    if (trimmed.includes(',')) {
      return trimmed
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((v) => v.replace(/:+$/, ''));
    }

    // Single value fallback
    return [trimmed.replace(/:+$/, '')].filter(Boolean);
  }

  return [];
};

/**
 * ✅ Normalize technology strings for chart bucketing:
 * - If token looks like "nginx: 1.22.1" -> "nginx"
 * - If token looks like "Server: nginx" -> "nginx"
 * - Otherwise returns the trimmed token
 */
const normalizeTechTokenForBucketing = (token) => {
  const s = String(token || '').trim();
  if (!s) return '';

  if (!s.includes(':')) return s;

  const [leftRaw, rightRaw] = s.split(':', 2);
  const left = String(leftRaw || '').trim();
  const right = String(rightRaw || '').trim();

  // If left side is a generic "label", we probably want the right side
  const genericKeys = new Set([
    'server',
    'web server',
    'http server',
    'framework',
    'platform',
    'powered by',
    'technology',
    'stack',
    'vendor',
    'product',
    'service',
  ]);

  if (genericKeys.has(left.toLowerCase()) && right) {
    return right;
  }

  // Default: treat the right side as a "value/version", use left side as the tech name
  return left || s;
};

// Very light web-stack bucketing (based on Technologies)
const bucketWebTech = (tech) => {
  const normalized = normalizeTechTokenForBucketing(tech);
  const t = String(normalized || '').toLowerCase();
  if (!t) return null;

  if (t.includes('nginx')) return 'nginx';
  if (t.includes('apache')) return 'apache httpd';
  if (t.includes('iis')) return 'microsoft iis';
  if (t.includes('caddy')) return 'caddy';
  if (t.includes('tomcat')) return 'apache tomcat';
  if (t.includes('haproxy')) return 'haproxy';
  if (t.includes('cloudflare')) return 'cloudflare';
  if (t.includes('kestrel')) return 'kestrel (.net)';
  if (t.includes('gunicorn')) return 'gunicorn';
  if (t.includes('uwsgi')) return 'uwsgi';
  if (t.includes('express')) return 'express (node)';
  if (t.includes('node')) return 'node.js';
  if (t.includes('next.js') || t.includes('nextjs')) return 'next.js';
  if (t.includes('nuxt')) return 'nuxt';
  if (t.includes('django')) return 'django';
  if (t.includes('flask')) return 'flask';
  if (t.includes('rails')) return 'ruby on rails';
  if (t.includes('laravel')) return 'laravel';
  if (t.includes('php')) return 'php';
  if (t.includes('spring')) return 'spring';
  if (t.includes('jetty')) return 'jetty';

  return null;
};

const ChipList = ({ items = [], max = 10, outlined = false, compact = false, allowWrap = false }) => {
  const list = (items || []).filter(Boolean);
  if (!list.length) return <Typography variant="body2">—</Typography>;

  const shown = list.slice(0, max);
  const hidden = list.length - shown.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap', // ✅ WRAP onto new lines
        gap: 0.6,
        alignItems: 'flex-start',
      }}
    >
      {shown.map((x) => (
        <Chip
          key={x}
          label={x}
          size={compact ? 'small' : 'small'}
          variant={outlined ? 'outlined' : 'filled'}
          sx={{
            maxWidth: '100%',
            ...(allowWrap
              ? {
                  // ✅ allow long labels (CPEs) to wrap inside the chip
                  height: 'auto',
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    display: 'block',
                    lineHeight: 1.15,
                    py: 0.35,
                  },
                }
              : {
                  // ports/services can stay 1-line
                  '& .MuiChip-label': { whiteSpace: 'nowrap' },
                }),
          }}
        />
      ))}

      {hidden > 0 && (
        <Chip
          label={`+${hidden}`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      )}
    </Box>
  );
};

/**
 * ✅ Description now uses a popup instead of expanding the table height
 */
const ExpandableText = ({ text = '', clampLines = 2, title = 'Description' }) => {
  const [open, setOpen] = useState(false);
  const content = String(text || '').trim();

  if (!content) return <Typography variant="body2">—</Typography>;

  const needsToggle = content.length > 160 || content.split('\n').length > clampLines;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: clampLines,
          overflow: 'hidden',
        }}
      >
        {content}
      </Typography>

      {needsToggle && (
        <>
          <Link
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            sx={{ fontSize: 12, mt: 0.5, display: 'inline-block' }}
          >
            View full
          </Link>

          <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>

            <DialogContent dividers>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap', // ✅ keeps new lines
                  wordBreak: 'break-word',
                  fontFamily: 'inherit',
                }}
              >
                {content}
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setOpen(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

const Assets = () => {
  const [assetsData, setAssetsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('host'); // host | port | technology | service | cpe
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  // Fetch assets (new format: { success: 1, assets: [...] })
  const getAssets = (token) => {
    fetch(`${BASEURL}/pentest/assets?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') {
          navigate('/');
          return;
        }

        const list = Array.isArray(data?.assets) ? data.assets : [];
        setAssetsData(list);
      })
      .catch((err) => console.error('Error fetching data:', err));
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) getAssets(token);
    else {
      console.error('No token found');
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, pentest_id]);

  // ---- Group by host (safe even if backend already aggregates) ----
  const groupedByHost = useMemo(() => {
    const acc = {};

    for (const a of assetsData) {
      const host = a?.host || '—';
      if (!acc[host]) {
        acc[host] = {
          host,
          description: '',
          ports: new Set(),
          services: new Set(),
          technologies: new Set(),
          cpes: new Set(),
        };
      }

      // Keep the first non-empty description, or the longer one
      const desc = String(a?.description || '').trim();
      if (desc) {
        if (!acc[host].description || desc.length > acc[host].description.length) {
          acc[host].description = desc;
        }
      }

      safeParseList(a?.ports).forEach((x) => acc[host].ports.add(x));
      safeParseList(a?.services).forEach((x) => acc[host].services.add(x));
      safeParseList(a?.technologies).forEach((x) => acc[host].technologies.add(x));
      safeParseList(a?.cpes).forEach((x) => acc[host].cpes.add(x));
    }

    return acc;
  }, [assetsData]);

  const groupedDataArray = useMemo(() => {
    return Object.values(groupedByHost).map((x) => ({
      host: x.host,
      description: x.description,
      ports: [...x.ports].filter(Boolean),
      services: [...x.services].filter(Boolean),
      technologies: [...x.technologies].filter(Boolean),
      cpes: [...x.cpes].filter(Boolean),
    }));
  }, [groupedByHost]);

  // ---- KPIs ----
  const kpis = useMemo(() => {
    const uniqueHosts = groupedDataArray.length;

    const allPorts = new Set();
    const allTechs = new Set();

    groupedDataArray.forEach((a) => {
      a.ports.forEach((p) => allPorts.add(String(p).trim()));
      a.technologies.forEach((t) => allTechs.add(String(t).trim()));
    });

    return {
      totalAssets: assetsData.length, // raw records returned
      uniqueHosts,
      uniquePorts: allPorts.size,
      uniqueTechs: allTechs.size,
    };
  }, [assetsData, groupedDataArray]);

  // ---- Charts ----
  const portCounts = useMemo(() => {
    const map = new Map();

    groupedDataArray.forEach((a) => {
      a.ports.forEach((p) => {
        const port = String(p).trim();
        if (!port) return;
        map.set(port, (map.get(port) || 0) + 1);
      });
    });

    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [groupedDataArray]);

  const webStackCounts = useMemo(() => {
    const map = new Map();

    groupedDataArray.forEach((a) => {
      a.technologies.forEach((token) => {
        // ✅ IMPORTANT: bucketWebTech internally strips ": value" now
        const bucket = bucketWebTech(token);
        if (!bucket) return;
        map.set(bucket, (map.get(bucket) || 0) + 1);
      });
    });

    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [groupedDataArray]);

  const portsBarData = useMemo(() => {
    return {
      labels: portCounts.map(([p]) => p),
      datasets: [
        {
          label: 'Hosts',
          data: portCounts.map(([, c]) => c),
          backgroundColor: lighten(PRIMARYCOLOR, 0.7),
          borderColor: lighten(PRIMARYCOLOR, 0.7),
          borderWidth: 1,
        },
      ],
    };
  }, [portCounts]);

  const webStackDoughnut = useMemo(() => {
    const labels = webStackCounts.map(([n]) => n);
    const data = webStackCounts.map(([, c]) => c);

    const base = [
      'rgba(25,118,210,0.7)',
      'rgba(76,175,80,0.7)',
      'rgba(255,152,0,0.7)',
      'rgba(156,39,176,0.7)',
      'rgba(244,67,54,0.7)',
      'rgba(63,81,181,0.7)',
      'rgba(0,150,136,0.7)',
      'rgba(121,85,72,0.7)',
    ];

    return {
      labels,
      datasets: [{ data, backgroundColor: base, borderWidth: 0 }],
    };
  }, [webStackCounts]);

  // ---- Filtering ----
  const filteredData = useMemo(() => {
    if (!searchTerm) return groupedDataArray;

    const q = searchTerm.toLowerCase();

    return groupedDataArray.filter((row) => {
      if (searchCategory === 'host') return (row.host || '').toLowerCase().includes(q);
      if (searchCategory === 'port') return row.ports.some((p) => String(p).toLowerCase().includes(q));
      if (searchCategory === 'technology') return row.technologies.some((t) => String(t).toLowerCase().includes(q));
      if (searchCategory === 'service') return row.services.some((s) => String(s).toLowerCase().includes(q));
      if (searchCategory === 'cpe') return row.cpes.some((c) => String(c).toLowerCase().includes(q));
      return true;
    });
  }, [groupedDataArray, searchCategory, searchTerm]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {/* ---------- Header & KPIs ---------- */}
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            <Card sx={{ ...surfaceCardSx, p: 2.6, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                Assets
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Discovered hosts, ports, services, and technologies aggregated from tool outputs.
              </Typography>
            </Card>

            {/* KPI Cards */}
            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <Typography variant="overline" color="text.secondary">
                Total Records
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {kpis.totalAssets}
              </Typography>
            </Card>

            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <Typography variant="overline" color="text.secondary">
                Unique Hosts
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {kpis.uniqueHosts}
              </Typography>
            </Card>

            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <Typography variant="overline" color="text.secondary">
                Unique Ports
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {kpis.uniquePorts}
              </Typography>
            </Card>

            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <Typography variant="overline" color="text.secondary">
                Technologies
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {kpis.uniqueTechs}
              </Typography>
            </Card>
          </Stack>

          {/* Charts */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Card sx={{ ...surfaceCardSx, p: 2.4, flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Top Ports
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Top {portCounts.length || 0} by host count
                </Typography>
              </Stack>

              {portCounts.length ? (
                <Box sx={{ height: 260 }}>
                  <Bar
                    data={portsBarData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No port data yet.
                </Typography>
              )}
            </Card>

            <Card sx={{ ...surfaceCardSx, p: 2.4, flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Web Stack
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Top {webStackCounts.length || 0} categories
                </Typography>
              </Stack>

              {webStackCounts.length ? (
                <Box sx={{ height: 260 }}>
                  <Doughnut
                    data={webStackDoughnut}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: 'bottom' } },
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recognizable web stack yet.
                </Typography>
              )}
            </Card>
          </Stack>

          <Divider />
        </Stack>

        {/* ---------- Filters ---------- */}
        <Card sx={{ ...surfaceCardSx, p: 2.4, mt: 0.5 }}>
          <Stack
            direction={{ xs: 'column', xl: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', xl: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Asset Inventory
              </Typography>
              <Chip
                size="small"
                label={`${filteredData.length}/${groupedDataArray.length || 0} shown`}
                sx={{ ml: 1, fontWeight: 700 }}
                variant="outlined"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Filter By</InputLabel>
                <Select value={searchCategory} label="Filter By" onChange={(e) => setSearchCategory(e.target.value)}>
                  <MenuItem value="host">Host</MenuItem>
                  <MenuItem value="port">Port</MenuItem>
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="service">Service</MenuItem>
                  <MenuItem value="cpe">CPE</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: { xs: '100%', md: 280 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button variant="outlined" onClick={() => setSearchTerm('')} sx={{ borderRadius: 999 }}>
                Clear
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* ---------- Table ---------- */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(PRIMARYCOLOR, 0.08),
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                width: '100%',
                '& .MuiTableCell-root': { borderColor: alpha(PRIMARYCOLOR, 0.06) },
                tableLayout: 'fixed', // ✅ makes widths actually work
              }}
            >
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: PRIMARYCOLOR, color: '#fff', fontWeight: 800, borderColor: PRIMARYCOLOR } }}>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: '16%' }}>Host</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: '28%' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: '10%' }}>Ports</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: '12%' }}>Services</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: '17%' }}>Technologies</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: '17%' }}>CPEs</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData.length ? (
                  filteredData.map((row) => (
                    <TableRow
                      key={row.host}
                      hover
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: alpha(PRIMARYCOLOR, 0.015) },
                        '&:hover': { backgroundColor: alpha(PRIMARYCOLOR, 0.05) },
                        '& td': { verticalAlign: 'top', py: 1.6 },

                      }}
                    >
                      {/* Host */}
                      <TableCell sx={{ pr: 1 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: alpha(PRIMARYCOLOR, 0.12),
                              color: PRIMARYCOLOR,
                              fontWeight: 800,
                            }}
                          >
                            {(row.host || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: '#10243E' }} noWrap>
                              {row.host}
                            </Typography>
                            <Typography sx={cellLabelSX}>
                              {row.ports.length} port{row.ports.length === 1 ? '' : 's'} •{' '}
                              {row.services.length} service{row.services.length === 1 ? '' : 's'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Description */}
                      <TableCell sx={{ pr: 1 }}>
                        <ExpandableText text={row.description} title={`Description — ${row.host}`} />
                      </TableCell>

                      {/* Ports */}
                      <TableCell sx={{ pr: 1 }}>
                        <ChipList items={row.ports} max={50} outlined compact />
                      </TableCell>

                      {/* Services */}
                      <TableCell sx={{ pr: 1 }}>
                        <ChipList items={row.services} max={50} outlined compact />
                      </TableCell>

                      {/* Technologies */}
                      <TableCell sx={{ pr: 1 }}>
                        <ChipList items={row.technologies} max={50} allowWrap />
                      </TableCell>

                      {/* CPEs */}
                      <TableCell sx={{ pr: 1 }}>
                        <ChipList items={row.cpes} max={50} allowWrap />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>No data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default Assets;

